import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api/client";
import { fetchBillingAccess } from "../lib/billingAccess";
import BillingNav from "../components/BillingNav";

// Money In: one EOB (billing PR #2, 2026-10-26; v1.1 PR #3, 2026-10-27) - SHADOW and DARK.
// Its lines, each line's proposed match to a charge (and how it was found), and
// the postings it would make. Approve writes them as "approved" in billing.* only;
// nothing here changes Raintree. Behind the server's billing gate like the home
// page: anyone else is sent home. Line ids go in the POST body, never in a URL.
// v1.1: Approve / Reject / Unapprove ask first; the bot's review flags show and keep
// a line out of "Select exact matches"; a held adjustment (zero-paid or PR) shows;
// candidates say when another line holds the charge, and using one asks first.

const METHOD = {
  exact: { label: "Exact", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  tie_billed_units: { label: "Tie: billed + units", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  // retired in v1.1 (a day off is a candidate now); kept so older proposals still read
  date_plus1: { label: "Date off: charge +1 day", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  date_minus1: { label: "Date off: charge −1 day", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  manual: { label: "Picked by hand", cls: "bg-sky-50 text-sky-700 border-sky-200" },
};

const STATUS = {
  unmatched: "Open",
  auto_matched: "Proposed",
  matched: "Approved",
  exception: "Rejected",
};

function money(v) {
  if (v === null || v === undefined) return "—";
  return Number(v).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export default function BillingRemit() {
  const { user } = useAuth();
  const { remitId } = useParams();
  const [allowed, setAllowed] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(() => new Set());

  useEffect(() => {
    let live = true;
    fetchBillingAccess(api, user?.id).then((ok) => {
      if (live) setAllowed(ok);
    });
    return () => {
      live = false;
    };
  }, [user?.id]);

  const load = useCallback(() => {
    setError("");
    return api
      .get(`/billing/remits/${remitId}`)
      .then((res) => {
        setData(res.data);
        setPicked(new Set());
      })
      .catch((e) => setError(e?.response?.status === 404 ? "No such EOB." : "Couldn't load this EOB."));
  }, [remitId]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  const lines = useMemo(() => data?.lines || [], [data]);
  const proposed = useMemo(() => lines.filter((l) => l.match_status === "auto_matched"), [lines]);
  const byId = useMemo(() => new Map(lines.map((l) => [l.remit_line_id, l])), [lines]);
  const pickedIn = (status) => [...picked].filter((id) => byId.get(id)?.match_status === status);

  async function act(path, body, say) {
    setBusy(true);
    setNote("");
    try {
      const res = await api.post(path, body);
      setNote(say(res.data));
      await load();
    } catch (e) {
      setNote(e?.response?.data?.detail || "That didn't go through.");
    } finally {
      setBusy(false);
    }
  }

  const propose = () =>
    act(`/billing/remits/${remitId}/propose`, {}, (d) =>
      `Proposed ${d.proposed} of ${d.looked_at} open lines (${d.postings} postings); ${d.left_open} left open` +
      (d.left_open_charge_claimed ? `, ${d.left_open_charge_claimed} because another line holds the charge` : "") +
      (d.left_open_same_charge_twice ? `, ${d.left_open_same_charge_twice} because two lines picked one charge` : "") +
      "." + (d.adjustments_held ? ` ${d.adjustments_held} adjustments held (zero-paid or PR).` : ""));
  const approve = () => {
    const ids = pickedIn("auto_matched");
    if (ids.length === 0 || !window.confirm(`Approve ${ids.length} proposed line(s)? You can unapprove later.`)) return;
    act(`/billing/remits/${remitId}/approve`, { remit_line_ids: ids }, (d) =>
      `Approved ${d.approved_lines} lines: payments ${money(d.payments)}, adjustments ${money(d.adjustments)}.` +
      (d.changed ? ` ${d.changed} changed since they were proposed: find matches again.` : "") +
      (d.skipped ? ` ${d.skipped} skipped (not proposed).` : ""));
  };
  const reject = () => {
    const ids = pickedIn("auto_matched");
    if (ids.length === 0 || !window.confirm(`Reject ${ids.length} proposed line(s)? They'll be open to match by hand.`)) return;
    act(`/billing/remits/${remitId}/reject`, { remit_line_ids: ids }, (d) =>
      `Rejected ${d.rejected_lines} proposed lines; they're open to match by hand.`);
  };
  const unapprove = () => {
    const ids = pickedIn("matched");
    if (ids.length === 0 || !window.confirm(`Unapprove ${ids.length} approved line(s)? Their postings become rejected.`)) return;
    act(`/billing/remits/${remitId}/unapprove`, { remit_line_ids: ids }, (d) =>
      `Unapproved ${d.unapproved_lines} lines; they're open to match by hand.`);
  };
  async function matchByHand(lineId, cand) {
    let confirmClaimed = false;
    if (cand.claimed_by_line_id) {
      if (!window.confirm(`Line ${cand.claimed_by_line_id} already holds this charge. Use it for this line too?`)) return;
      confirmClaimed = true;
    }
    await act(`/billing/remits/${remitId}/match`,
      { remit_line_id: lineId, charge_id: cand.charge_id, confirm_claimed: confirmClaimed }, (d) =>
      d.matched ? "Matched by hand; approve it like the others."
        : d.reason === "claimed" ? `Line ${d.claimed_by_line_id} holds that charge now; reload and choose again.`
        : "That line isn't open any more.");
  }

  function toggle(id) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (allowed === null) {
    return <div className="flex h-full items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!allowed) return <Navigate to="/" replace />;

  const r = data?.remit;
  const c = data?.counts || {};
  return (
    <div className="p-8" data-testid="billing-remit">
      <BillingNav current="eob" />
      <div className="text-sm text-slate-500">
        <Link to="/billing" className="text-sky-700 hover:underline">Billing</Link>{" / "}
        <Link to="/billing/eob" className="text-sky-700 hover:underline">Money In</Link>{" / "}EOB #{remitId}
      </div>
      <Link to="/billing/eob" className="mt-2 inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50" data-testid="billing-remit-back">
        ← Back to all EOBs
      </Link>
      <h1 className="mt-2 text-2xl font-semibold text-slate-900">EOB #{remitId}</h1>
      <p className="mt-1 text-sm text-slate-500">
        Shadow mode. Approving writes billing postings for review only; nothing here changes Raintree.
      </p>
      {error && <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">{error}</div>}
      {r && (
        <div className="mt-4 flex flex-wrap gap-6 text-sm text-slate-600">
          <span>{r.remit_type === "payment_list" ? "Payment list" : "EOB scan"}</span>
          <span>Received {r.received_at || "—"}</span>
          {r.check_number && <span>Check {r.check_number}</span>}
          <span>Total paid {money(r.total_paid)}</span>
          <span data-testid="billing-remit-counts">
            Open {c.unmatched || 0} · Proposed {c.auto_matched || 0} · Approved {c.matched || 0} · Rejected {c.exception || 0}
          </span>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button className="rounded-lg bg-slate-800 px-3 py-1.5 text-sm text-white disabled:opacity-40"
                disabled={busy || !data} onClick={propose} data-testid="billing-propose">
          Find matches
        </button>
        <button className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-40"
                disabled={busy || proposed.length === 0}
                onClick={() => setPicked(new Set(proposed.filter((l) => l.charge_match_method === "exact" && !l.review_flags).map((l) => l.remit_line_id)))}
                data-testid="billing-select-exact">
          Select exact matches
        </button>
        <button className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm text-white disabled:opacity-40"
                disabled={busy || pickedIn("auto_matched").length === 0} onClick={approve} data-testid="billing-approve">
          Approve selected ({pickedIn("auto_matched").length})
        </button>
        <button className="rounded-lg border border-rose-200 px-3 py-1.5 text-sm text-rose-700 disabled:opacity-40"
                disabled={busy || pickedIn("auto_matched").length === 0} onClick={reject} data-testid="billing-reject">
          Reject selected
        </button>
        <button className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 disabled:opacity-40"
                disabled={busy || pickedIn("matched").length === 0} onClick={unapprove} data-testid="billing-unapprove">
          Unapprove selected ({pickedIn("matched").length})
        </button>
        {note && <span className="text-sm text-slate-600" data-testid="billing-note">{note}</span>}
      </div>

      {lines.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-xs" data-testid="billing-lines">
            <thead className="bg-slate-50 text-left uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2" />
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Patient</th>
                <th className="px-3 py-2">DOS</th>
                <th className="px-3 py-2">CPT</th>
                <th className="px-3 py-2 text-right">Units</th>
                <th className="px-3 py-2 text-right">Billed</th>
                <th className="px-3 py-2 text-right">Paid</th>
                <th className="px-3 py-2 text-right">Adj.</th>
                <th className="px-3 py-2">Codes / flags</th>
                <th className="px-3 py-2">Match</th>
                <th className="px-3 py-2">Charge</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lines.map((l) => {
                const m = METHOD[l.charge_match_method];
                const open = l.match_status === "unmatched" || l.match_status === "exception";
                return (
                  <tr key={l.remit_line_id} className={l.match_status === "matched" ? "bg-emerald-50/40" : ""}>
                    <td className="px-3 py-2">
                      {(l.match_status === "auto_matched" || l.match_status === "matched") && (
                        <input type="checkbox" checked={picked.has(l.remit_line_id)}
                               onChange={() => toggle(l.remit_line_id)} />
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-500">{l.line_number ?? "—"}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800">{l.patient_name_raw || "—"}</div>
                      <div className="text-slate-400">{l.patient_pn || ""}{l.has_patient ? "" : " · no patient"}</div>
                    </td>
                    <td className="px-3 py-2">{l.date_of_service || "—"}</td>
                    <td className="px-3 py-2">{l.cpt_code || "—"}</td>
                    <td className="px-3 py-2 text-right">{l.units ?? "—"}</td>
                    <td className="px-3 py-2 text-right">{money(l.billed_amount)}</td>
                    <td className="px-3 py-2 text-right">{money(l.paid_amount)}</td>
                    <td className="px-3 py-2 text-right">
                      {money(l.adjustment_amount)}
                      {l.adjustment_held && (
                        <div className="text-amber-700" title="Not proposed: a zero-paid denial or a patient-responsibility amount">held</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {[l.ansi_carc, l.remark_codes, l.payor_action_code].filter(Boolean).join(" ") || "—"}
                      {l.review_flags && (
                        <div className="mt-1 inline-block rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-amber-800"
                             data-testid="billing-review-flags">{l.review_flags}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-slate-700">{STATUS[l.match_status] || l.match_status}</div>
                      {m && <span className={`mt-1 inline-block rounded border px-1.5 py-0.5 ${m.cls}`}>{m.label}</span>}
                    </td>
                    <td className="px-3 py-2">
                      {l.charge_id ? (
                        <div className="text-slate-700">
                          {l.charge_date_of_service} · {l.charge_cpt_code} · {l.charge_units} u · {money(l.charge_billed_amount)}
                          <div className="text-slate-400">
                            posts {money(l.posting_payment)} + adj {money(l.posting_adjustment)} · Raintree paid {money(l.charge_rt_payments)}
                          </div>
                          {l.case_id ? (
                            <Link to={`/billing/case/${l.case_id}`} className="text-blue-600 hover:underline"
                                  data-testid={`remit-open-case-${l.remit_line_id}`}>
                              Open the case
                            </Link>
                          ) : null}
                        </div>
                      ) : open && l.candidates && l.candidates.length > 0 ? (
                        <div className="space-y-1">
                          {l.candidates.map((cand) => (
                            <div key={cand.charge_id} className="flex items-center gap-2">
                              <span className="text-slate-600">
                                {cand.date_of_service} ({cand.day_offset > 0 ? "+" : ""}{cand.day_offset}d) · {cand.cpt_code} · {cand.units} u · {money(cand.billed_amount)} · Raintree paid {money(cand.rt_payments_amount)}
                                {cand.claimed_by_line_id && (
                                  <span className="ml-1 text-amber-700" data-testid="billing-claimed">claimed by line {cand.claimed_by_line_id}</span>
                                )}
                              </span>
                              <button className="rounded border border-slate-200 px-1.5 py-0.5 text-blue-700 disabled:opacity-40"
                                      disabled={busy} onClick={() => matchByHand(l.remit_line_id, cand)}>
                                Use
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
